import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { AlertCircle, Clock, RefreshCw, ChevronDown } from 'lucide-react';

const RebalancingAlertHistory = () => {
  const { get, post } = useApi();
  const [alerts, setAlerts] = useState(null);
  const [history, setHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [alertRes, historyRes] = await Promise.allSettled([
          get('/rebalancing/active-alerts'),
          get('/rebalancing/history?limit=10'),
        ]);

        if (alertRes.status === 'fulfilled') {
          setAlerts(alertRes.value);
        }
        if (historyRes.status === 'fulfilled') {
          setHistory(historyRes.value.history || []);
        }
      } catch (error) {
        console.error('Failed to load rebalancing data:', error);
      } finally {
        setLoaded(true);
      }
    };

    loadData();
  }, [get]);

  const handleCheckRebalancing = async () => {
    setChecking(true);
    try {
      // Log the rebalancing check
      const logRes = await post('/rebalancing/log', {
        triggerReason: 'MANUAL_REBALANCE',
        actionTaken: 'ALERT_ONLY',
        notes: 'Manual rebalancing check triggered by user',
      });

      if (logRes) {
        // Reload data
        const [alertRes, historyRes] = await Promise.allSettled([
          get('/rebalancing/active-alerts'),
          get('/rebalancing/history?limit=10'),
        ]);

        if (alertRes.status === 'fulfilled') {
          setAlerts(alertRes.value);
        }
        if (historyRes.status === 'fulfilled') {
          setHistory(historyRes.value.history || []);
        }
      }
    } catch (error) {
      console.error('Failed to check rebalancing:', error);
      alert('Failed to check rebalancing. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  if (!loaded) {
    return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Rebalancing Alerts & History</h1>
          <p className="text-gray-500">Monitor portfolio drift and rebalancing actions</p>
        </div>
        <button
          onClick={handleCheckRebalancing}
          disabled={checking}
          className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition"
        >
          <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Checking...' : 'Check Rebalancing'}
        </button>
      </div>

      {/* Info Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
        <h3 className="font-semibold text-blue-900 mb-2">💡 How to Use</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li><strong>Check Rebalancing:</strong> Click the "Check Rebalancing" button to analyze your current portfolio and detect any drift from target allocations</li>
          <li><strong>View Alerts:</strong> If drift is detected (&gt;5% deviation), you'll see a yellow alert box with asset-wise drift details</li>
          <li><strong>History Log:</strong> All rebalancing checks are stored below with timestamps, drift details, and before/after allocation comparisons</li>
        </ul>
      </div>

      {/* Active Alerts Section */}
      {alerts?.needsRebalancing && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-yellow-900 mb-3">Active Rebalancing Alert</h2>
              <p className="text-sm text-yellow-800 mb-4">
                Trigger: <span className="font-medium">{alerts.triggerReason?.replace(/_/g, ' ')}</span> • 
                Last checked: <span className="font-medium">{new Date(alerts.lastAlertTime).toLocaleDateString()}</span>
              </p>
              
              {alerts.alerts && alerts.alerts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {alerts.alerts.map((alert, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-yellow-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800">{alert.asset}</span>
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          alert.status === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                          alert.status === 'WARNING' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {alert.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>Target:</span>
                          <span className="font-medium">{alert.target}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Previous:</span>
                          <span className="font-medium">{alert.previous}%</span>
                        </div>
                        <div className={`flex justify-between font-semibold ${
                          alert.drift > 0 ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          <span>Drift:</span>
                          <span>{alert.drift > 0 ? '+' : ''}{alert.drift}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-yellow-700">No alerts detected</p>
              )}

              <button className="mt-4 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Regenerate Portfolio
              </button>
            </div>
          </div>
        </div>
      )}

      {!alerts?.needsRebalancing && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 bg-green-500 rounded-full"></div>
              <p className="text-green-800">✓ Portfolio is well-balanced. No rebalancing needed.</p>
            </div>
            <button
              onClick={handleCheckRebalancing}
              disabled={checking}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition"
            >
              <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Checking...' : 'Check Again'}
            </button>
          </div>
        </div>
      )}

      {/* History Section */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary-600" />
          Rebalancing History
        </h2>

        {history.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No rebalancing history yet</p>
        ) : (
          <div className="space-y-3">
            {history.map((event) => (
              <div key={event._id} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedId(expandedId === event._id ? null : event._id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-3 flex-1 text-left">
                    <div className="flex-shrink-0">
                      {event.triggerReason === 'DRIFT_DETECTED' ? (
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      ) : event.triggerReason === 'MANUAL_REBALANCE' ? (
                        <RefreshCw className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{event.triggerReason.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-gray-500">{new Date(event.createdAt).toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mr-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      event.actionTaken === 'AUTO_REBALANCED' ? 'bg-green-100 text-green-700' :
                      event.actionTaken === 'PENDING_USER_ACTION' ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {event.actionTaken?.replace(/_/g, ' ')}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition ${expandedId === event._id ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>

                {/* Expanded Details */}
                {expandedId === event._id && (
                  <div className="bg-gray-50 border-t border-gray-200 px-4 py-4 space-y-4">
                    {/* Drift Alerts */}
                    {event.driftAlerts?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-3">Asset Class Drift</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {event.driftAlerts.map((alert, idx) => (
                            <div key={idx} className="bg-white rounded p-3 border border-gray-200">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium text-gray-800">{alert.asset}</span>
                                <span className={`text-xs font-bold px-2 py-1 rounded ${
                                  alert.status === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                                  alert.status === 'WARNING' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {alert.status}
                                </span>
                              </div>
                              <div className="text-xs space-y-1 text-gray-600">
                                <div className="flex justify-between">
                                  <span>Target:</span>
                                  <span className="font-medium">{alert.target}%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Previous:</span>
                                  <span className="font-medium">{alert.previous}%</span>
                                </div>
                                <div className={`flex justify-between font-semibold ${
                                  alert.drift > 0 ? 'text-red-600' : 'text-blue-600'
                                }`}>
                                  <span>Drift:</span>
                                  <span>{alert.drift > 0 ? '+' : ''}{alert.drift}%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Before/After Comparison */}
                    {event.beforeAllocation?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-3">Portfolio Composition</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Before */}
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Before</h5>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {event.beforeAllocation.map((alloc, idx) => (
                                <div key={idx} className="flex justify-between text-xs bg-white p-2 rounded border border-gray-200">
                                  <span className="font-medium">{alloc.symbol || alloc.assetClass}</span>
                                  <span>{alloc.weight}%</span>
                                </div>
                              ))}
                            </div>
                            {event.beforeMetrics && (
                              <div className="mt-3 pt-3 border-t border-gray-200 text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span>Expected Return:</span>
                                  <span className="font-medium">{((event.beforeMetrics.expectedReturn || 0) * 100).toFixed(2)}%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Volatility:</span>
                                  <span className="font-medium">{((event.beforeMetrics.volatility || 0) * 100).toFixed(2)}%</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* After */}
                          {event.afterAllocation?.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">After</h5>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {event.afterAllocation.map((alloc, idx) => (
                                  <div key={idx} className="flex justify-between text-xs bg-green-50 p-2 rounded border border-green-200">
                                    <span className="font-medium">{alloc.symbol || alloc.assetClass}</span>
                                    <span>{alloc.weight}%</span>
                                  </div>
                                ))}
                              </div>
                              {event.afterMetrics && (
                                <div className="mt-3 pt-3 border-t border-gray-200 text-xs space-y-1">
                                  <div className="flex justify-between">
                                    <span>Expected Return:</span>
                                    <span className="font-medium text-green-600">{((event.afterMetrics.expectedReturn || 0) * 100).toFixed(2)}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Volatility:</span>
                                    <span className="font-medium text-green-600">{((event.afterMetrics.volatility || 0) * 100).toFixed(2)}%</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {event.notes && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-600"><span className="font-medium">Notes:</span> {event.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RebalancingAlertHistory;
